import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type Props = {
  registrationId: string | null;
  teamName?: string;
  /** Comped rows offer the reverse action instead. */
  comped: boolean;
  balance: number;
  compReason?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged?: () => void;
};

const money = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

/**
 * Comping is a money event: the edge function writes a registration_payments
 * adjustment so the ledger still reconciles, and un-comping voids that
 * adjustment rather than deleting it. Both require a reason.
 */
export default function CompRegistrationDialog({
  registrationId,
  teamName,
  comped,
  balance,
  compReason,
  open,
  onOpenChange,
  onChanged,
}: Props) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open, registrationId]);

  const submit = async () => {
    if (!registrationId) return;
    if (!reason.trim()) {
      toast.error("A reason is required");
      return;
    }
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("comp-registration", {
      body: {
        action: comped ? "uncomp" : "comp",
        registration_id: registrationId,
        reason: reason.trim(),
      },
    });
    setBusy(false);
    const err = error?.message || (data as any)?.error;
    if (err) {
      toast.error(err);
      return;
    }
    toast.success(comped ? "Comp removed" : "Team comped");
    onChanged?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{comped ? "Remove comp" : "Comp this team"}</DialogTitle>
          <DialogDescription>
            {comped ? (
              <>
                This puts {teamName || "the team"}'s balance back. The original comp adjustment is
                voided in the ledger, never deleted.
                {compReason ? <> Original reason: “{compReason}”.</> : null}
              </>
            ) : (
              <>
                {teamName || "This team"} owes {money(balance)}. Comping writes a{" "}
                {money(balance)} adjustment to the payment ledger and takes the balance to $0.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="comp-reason">Reason</Label>
          <Textarea
            id="comp-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={comped ? "Why is the comp being removed?" : "Why is this team comped?"}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || !reason.trim()}>
            {busy ? "Working…" : comped ? "Remove comp" : "Comp team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
